import { apiEndpoint, getZaplifySdk } from '@zaplify/sdk';
import { getEmailDomain } from '@zaplify/utils';
import { batch } from 'react-redux-latest';
import { Dispatch } from 'redux';
// import { PaymentStatus } from '../../components/pick-plan-modal/enums/billing';
import { LocalStorageKeys } from '../../config';
import { paths } from '../../routes/paths';
import { userActionTypes } from '../actionTypes';
import { authStates } from '../authStates';
import { AsyncThunkConfig } from '../types';
import { EventType } from '@azure/msal-browser';
import { IPublicClientApplication } from '@azure/msal-browser';

const getErrorMessage = (errorCode) => {
    switch (errorCode) {
        case 'auth/wrong-password':
            return 'Email or password incorrect';
        case 'auth/user-not-found':
            return 'The password or email you entered is incorrect.';
        case 'auth/email-already-in-use':
            return 'This email is already linked to a Zaplify account. Please sign in with your email address and password.';
        case 'auth/too-many-requests':
            return 'Too many invalid requests, please try again later';
        default:
            return 'An unexpected error occurred. Please try again later';
    }
};

export const loginSuccess = (user) => {
    return {
        type: userActionTypes.LOGIN_SUCCESS,
        firebaseUser: user,
    };
};

const loginError = (firebaseError) => {
    return {
        type: userActionTypes.LOGIN_FAILED,
        authErrorMessage: getErrorMessage(firebaseError.code),
    };
};

export const fetchUserSuccess = (user) => {
    return {
        type: userActionTypes.FETCH_USER_SUCCESS,
        zaplifyUser: user,
    };
};

const fetchUserError = (errorMessage) => {
    return {
        type: userActionTypes.FETCH_USER_FAILED,
        authErrorMessage: errorMessage,
    };
};

const logoutSuccess = () => {
    return {
        type: userActionTypes.LOGOUT_SUCCESS,
    };
};

export const shouldPreventRedirectToLogin = (preventRedirectToLogin) => {
    return {
        type: userActionTypes.PREVENT_REDIRECT_TO_LOGIN,
        preventRedirectToLogin: preventRedirectToLogin,
    };
};

export const logoutError = (errorMessage) => {
    return {
        type: userActionTypes.LOGOUT_FAILED,
        authErrorMessage: errorMessage,
    };
};

const registerSuccess = (user) => {
    return {
        type: userActionTypes.REGISTER_SUCCESS,
        firebaseUser: user,
    };
};

const registerError = (firebaseError) => {
    return {
        type: userActionTypes.REGISTER_FAILED,
        authErrorMessage: getErrorMessage(firebaseError.code),
        typeOfError: firebaseError.code,
    };
};

const registerCustomError = (errorMessage, typeOfError) => {
    return {
        type: userActionTypes.REGISTER_FAILED,
        authErrorMessage: errorMessage,
        typeOfError: typeOfError,
    };
};

export const setAuthState = (value) => {
    return {
        type: userActionTypes.AUTH_STATE,
        authState: value,
    };
};

const startLoading = () => {
    return {
        type: userActionTypes.START_LOADING,
    };
};

const stopLoading = () => {
    return {
        type: userActionTypes.STOP_LOADING,
    };
};

export const cleanAuthError = () => {
    return {
        type: userActionTypes.CLEAN_AUTH_ERROR,
    };
};

export const updateVerifiedUserAuthState =
    (
        msalInstance: IPublicClientApplication,
        navigate?: any,
        selectedOrganizationId?: string,
        navigateToLinkedInSetup?: boolean
    ) =>
    async (dispatch: Dispatch, getState, {}: AsyncThunkConfig['extra']) => {
        const msId = msalInstance.getActiveAccount()?.localAccountId;
        const token = msalInstance.getActiveAccount()?.idToken;
        console.log('active account', msalInstance.getActiveAccount());

        console.log('got ms token', msId, token);
        console.log('updateVerifiedUserAuthState', navigateToLinkedInSetup);
        const zaplifySdk = getZaplifySdk(apiEndpoint, msalInstance);
        let accounts = [];

        try {
            dispatch(startLoading());

            const accountsPromise = zaplifySdk.profiles.user.getUserAccounts();
            const userPromise = zaplifySdk.profiles.user.getUserByFirebaseId();
            const [accountsResponse, userResponse] = await Promise.allSettled([accountsPromise, userPromise]);

            if (accountsResponse.status === 'fulfilled' && accountsResponse.value) {
                accounts = accountsResponse.value.filter((account) => !!account.userId);
            } else {
                accounts = [];
            }

            // if (userResponse.status === 'rejected') {
            //     throw new Error('Error fetching user');
            // }

            const isLoggedIn = !!msId;
            const nbrOfUserAccounts = accounts.length || 0;
            const zaplifyUser = userResponse.status === 'fulfilled' ? userResponse?.value : undefined;
            const zaplifyUserOrganizationId = 'test';
            const didSelectOrg = !!zaplifyUser?.userOrganizationId;

            const logout = () => {
                dispatch(setAuthState(authStates.LOGGED_OUT));
                // navigate(paths.HOME);
            };

            const shouldCreateUser = () => {
                dispatch(setAuthState(authStates.NOT_CONFIGURED));
                navigate(paths.CONFIGURE_PROFILE);
            };

            const shouldSelectOrganization = () => {
                // dispatch(fetchUserSuccess(zaplifyUser));
                dispatch(setAuthState(authStates.CHOOSE_ORGANIZATION));
                navigate(paths.ACCOUNTS);
            };

            const shouldSwitchToOrganization = async (organizationId: string) => {
                // const authAsUser = localStorage.getItem(LocalStorageKeys.AUTHENTICATE_AS_USER);
                // await zaplifySdk.profiles.authentication.chooseOrganization(organizationId, authAsUser);
                // await fbUser.getIdToken(true);
                const userInSelectedOrg = await zaplifySdk.profiles.user.getUserByFirebaseId();
                await getInitialLoad({ value: userInSelectedOrg }, dispatch, getState);
            };

            if (!isLoggedIn) {
                console.log('is logged out');
                logout();
                return;
            } else if (didSelectOrg) {
                console.log('did select organization');
                await shouldSwitchToOrganization(selectedOrganizationId);
                return;
            } else if (nbrOfUserAccounts === 0) {
                console.log('did not select org and must complete onboarding');
                shouldCreateUser();
                return;
            } else if (nbrOfUserAccounts === 1 && zaplifyUserOrganizationId) {
                console.log('did not select org but has only one org. autoslecting org...');
                await shouldSwitchToOrganization(zaplifyUserOrganizationId);
            } else {
                console.log('user has multiple organizations. should select org');
                shouldSelectOrganization();
            }
        } catch (error) {
            dispatch(
                fetchUserError(
                    'We encountered an issue while fetching your user data. Please try again or contact support if the problem persists.'
                )
            );
            // @TODO Display some error page (like with a refresh button or something) instead that actually helps the user
            dispatch(setAuthState(authStates.CHOOSE_ORGANIZATION));
        } finally {
            dispatch(stopLoading());
        }
    };

export const updateUser = (msalInstance: IPublicClientApplication) => async (dispatch) => {
    const profilesSdk = getZaplifySdk(apiEndpoint, msalInstance).profiles;

    const promises = [profilesSdk.user.getUserByFirebaseId()];

    const [zaplifyUserResult] = await Promise.allSettled(promises);

    batch(() => {
        if (zaplifyUserResult.status === 'fulfilled') {
            dispatch(fetchUserSuccess(zaplifyUserResult.value));
        } else {
            console.error(zaplifyUserResult.reason);
        }
    });

    return true;
};

export const getInitialLoad = async (userResponse, dispatch, getState) => {
    localStorage.setItem(`initial_load`, 'true');

    batch(() => {
        dispatch(fetchUserSuccess(userResponse.value));
        dispatch(setAuthState(authStates.FULLY_CONFIGURED));
        // dispatch(getOrganizationRewards());
        localStorage.removeItem(`initial_load`);
    });
};

export const updateUserAuthState = (msalInstance: IPublicClientApplication, navigate) => async (dispatch) => {
    dispatch(updateVerifiedUserAuthState(msalInstance, navigate));
    // if (isEmailVerified) {
    //     dispatch(updateVerifiedUserAuthState(firebaseId, navigate));
    // } else {
    //     dispatch(setAuthState(authStates.NOT_VERIFIED));
    // }
};

export const logoutUser =
    () =>
    async (dispatch, getState, { getFirebase }) => {
        try {
            // @TODO Hit choose organization endpoint with no organization id.
            // So that next time user logs in, they can choose org.
            // For global admins this will also ensure that they have exited impersonation the next time they login.
            dispatch({ type: 'RESET_STORE' });
            dispatch(logoutSuccess());
            clearLocalStorageKeys();
        } catch (error) {
            dispatch(logoutError('error logging out'));
        }
    };

export const clearLocalStorageKeys = () => {
    Object.keys(LocalStorageKeys).forEach((key) => localStorage.removeItem(LocalStorageKeys[key]));
};

export const logoutAndRedirect =
    () =>
    async (dispatch, getState, { getFirebase }) => {
        try {
            dispatch(shouldPreventRedirectToLogin(true));
            // instance.logoutPopup().catch((e) => {
            //     console.error(e);
            // });
            // @TODO Hit choose organization endpoint with no organization id.
            // So that next time user logs in, they can choose org.
            // For global admins this will also ensure that they have exited impersonation the next time they login.
            localStorage.removeItem(LocalStorageKeys.AUTHENTICATE_AS_USER);
            clearLocalStorageKeys();
            window.open('https://happy-island-04810e603.5.azurestaticapps.net/', '_self');
        } catch (error) {
            dispatch(logoutError('error logging out'));
        }
    };

export const refreshCurrentUserData = (msalInstance: IPublicClientApplication) => async (dispatch, getState) => {
    try {
        const zaplifyUser = await getZaplifySdk(apiEndpoint, msalInstance).profiles.user.getUserByFirebaseId();
        if (zaplifyUser) {
            dispatch(fetchUserSuccess(zaplifyUser));
        }
    } catch (error) {
        console.log('refreshUserData error');
    }
};

export interface ICreateOrgState {
    action: 'none' | 'create' | 'join';
    domain: string;
    companyName?: string;
}

export const configureProfile =
    (msalInstance: IPublicClientApplication, createOrgState: ICreateOrgState, profile, navigate) =>
    async (dispatch, getState, { getFirebase }) => {
        try {
            const msUser = msalInstance.getActiveAccount();
            const zaplifySdk = getZaplifySdk(apiEndpoint, msalInstance);

            try {
                let zaplifyUser;
                const createOrganizationName = createOrgState.action === 'create' && createOrgState.companyName;
                const createOrganizationDomain = createOrgState.action === 'create' && createOrgState.domain;
                const joinOrganizationName = createOrgState.action === 'join' && createOrgState.companyName;
                const isCreatingOrganization = !!createOrganizationName && !joinOrganizationName;

                if (isCreatingOrganization) {
                    // Create organization
                    const payload = {
                        firebaseId: msUser.localAccountId,
                        email: msUser.idTokenClaims.email as string,
                        firstName: profile.firstName,
                        lastName: profile.lastName,
                        organization: {
                            name: createOrganizationName,
                            domain: createOrganizationDomain || getEmailDomain(msUser.idTokenClaims.email as string),
                            website: profile.website,
                        },
                    };

                    zaplifyUser = await zaplifySdk.profiles.user.createUserOrganization(payload);
                } else {
                    // Join organization
                    const payload = {
                        firebaseId: msUser.localAccountId,
                        email: msUser.idTokenClaims.email as string,
                        firstName: profile.firstName,
                        lastName: profile.lastName,
                    };

                    zaplifyUser = await zaplifySdk.profiles.user.addUser(payload);
                }

                if (!zaplifyUser) {
                    return;
                }

                // localStorage.removeItem(LocalStorageKeys.JOIN_ORGANIZATION_NAME);

                batch(() => {
                    dispatch(fetchUserSuccess(zaplifyUser));
                    //Always navigating to LinkedIn step after configuring profile for now.
                    dispatch(updateVerifiedUserAuthState(msalInstance, navigate, zaplifyUser.userOrganizationId, true));
                });

                // dispatch(stopLoading());

                return zaplifyUser;
            } catch (error: any) {
                dispatch(fetchUserError('error fetching user'));

                throw new Error(error.message || 'error creating user');
            }
        } catch (error) {
            dispatch(stopLoading());
            throw new Error('Error configuring profile. Try again later', {
                cause: error,
            });
        }
    };

export const startAuthListener = (navigate, msalInstance) => async (dispatch, getState) => {
    msalInstance.instance.addEventCallback((event) => {
        console.log('event', event);
        // debugger;
        if (event.eventType === EventType.LOGIN_SUCCESS && event.payload && event.payload.account) {
            const account = event.payload.account;
            if (account) {
                msalInstance.instance.setActiveAccount(account);
                dispatch(loginSuccess(account));
                dispatch(updateUserAuthState(msalInstance.instance, navigate));
            }
        } else if (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) {
            const account = msalInstance.instance.getActiveAccount();
            if (!account) {
                dispatch(logoutSuccess());
                return;
            }
            msalInstance.instance.setActiveAccount(account);
            dispatch(loginSuccess(account));
            dispatch(updateUserAuthState(msalInstance.instance, navigate));
        } else if (event.eventType === EventType.HANDLE_REDIRECT_START) {
            dispatch(logoutSuccess());
        }
    });
};
